import { useCallback, useContext, useRef, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import YAML from 'yamljs';
import { z, ZodError, ZodIssueCode } from 'zod';
import {
  faFileCheck,
  faFileXmark,
  faTriangleExclamation,
  faUpload,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons';

import { toast } from '@features';
import { cn } from '@utils';

import { Button, Icon, Modal, ModalRef, Render, Tooltip } from '@components';
import { SchemaContext } from '@components/Providers';

type Props = {
  templateName: string;
  templateVersion: string;
  schema: z.ZodObject<any>;
};

export const SchemaImportConfig = ({
  schema,
  templateName,
  templateVersion,
}: Props) => {
  const { onConfigApply } = useContext(SchemaContext);

  const modalRef = useRef<ModalRef>(null);

  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<object | null>(null);
  const [errors, setErrors] = useState<z.ZodIssue[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length !== 0) {
        toast.error(
          `File Import App Configuration error: ${fileRejections[0].errors[0].message}`,
        );
        return;
      }

      const file = acceptedFiles[0];

      if (!file) {
        toast.error('No file selected');
        return;
      }

      setData(null);
      setFile(null);
      setErrors([]);

      const reader = new FileReader();

      reader.onload = () => {
        try {
          const yamlText = reader.result as string;
          const parsed = YAML.parse(yamlText);

          setFile(file);

          if (parsed.template_name !== templateName) {
            throw new ZodError([
              {
                code: ZodIssueCode.custom,
                message: `Invalid template name: expected "${templateName}", got "${parsed.template_name}"`,
                path: [],
              },
            ]);
          }

          if (parsed.template_version !== templateVersion) {
            throw new ZodError([
              {
                code: ZodIssueCode.custom,
                message: `Invalid template version: expected "${templateVersion}", got "${parsed.template_version}"`,
                path: [],
              },
            ]);
          }

          if (!parsed.input) {
            throw new ZodError([
              {
                code: ZodIssueCode.custom,
                message: 'YAML must contain "input" field',
                path: ['input'],
              },
            ]);
          }

          const validated = schema.parse(parsed.input);
          setData({ ...validated, displayName: parsed.display_name });
        } catch (error: { errors?: z.ZodIssue[] } | any) {
          if (Array.isArray(error.errors)) {
            setErrors(error.errors);
          } else {
            toast.error(`Error parsing YAML: ${error.message}`);
          }
        }
      };

      reader.onerror = () => {
        toast.error('Error reading file');
      };

      reader.readAsText(file);
    },
    [schema, templateName, templateVersion],
  );

  const { getRootProps, getInputProps, isDragAccept, isDragActive } =
    useDropzone({
      onDrop,
      multiple: false,
      maxSize: 0.1 * 1024 * 1024, // 100 KB
      accept: {
        'application/x-yaml': ['.yaml', '.yml'],
        'text/yaml': ['.yaml', '.yml'],
      },
    });

  const handleSave = () => {
    if (!data) {
      return;
    }

    onConfigApply(data);
    modalRef.current?.close();
  };

  const handleClose = () => {
    setFile(null);
    setData(null);
    setErrors([]);
  };

  const content = (
    <Modal.Content title="Import App Configuration" className="w-[520px]">
      <Render if={!file}>
        <div
          {...getRootProps({
            className: cn(
              'relative flex cursor-pointer items-center justify-center gap-3 rounded-lg border border-dashed border-neural-03 py-8 transition-colors hover:bg-background',
              {
                'border-primary bg-background': isDragActive || isDragAccept,
              },
            ),
          })}
        >
          <input {...getInputProps()} />
          <Icon icon={faCloudArrowUp} className="text-[20px] text-neural-04" />
          <p className="text-sm text-neural-04">
            Drag and drop your file or{' '}
            <span className="text-primary">browse</span>
          </p>
        </div>
      </Render>

      <Render if={file}>
        <div className="flex items-center gap-2">
          <Icon
            icon={errors.length > 0 ? faFileXmark : faFileCheck}
            className={cn('text-[20px]', {
              'text-red-700': errors.length > 0,
              'text-green-600': errors.length === 0,
            })}
          />

          <p className="text-sm text-neural-04">
            {file?.name} ({((file?.size ?? 0) / 1024).toFixed(2)} KB)
          </p>

          <Button
            theme
            onClick={() => {
              setFile(null);
              setData(null);
              setErrors([]);
            }}
          >
            <Icon
              icon={faXmark}
              className="text-[16px] text-neural-04 transition-colors hover:text-red-600"
            />
          </Button>
        </div>
      </Render>

      <Render if={errors}>
        <div className="mt-5">
          <h4 className="flex items-center gap-[6px] text-red-700">
            <Icon
              icon={faTriangleExclamation}
              className="inline-flex text-[16px] text-red-700"
            />
            Errors:
          </h4>

          <ul className="mt-1 list-inside list-disc space-y-1 pl-4 text-sm text-red-700">
            {errors.map((error) => (
              <li
                key={`${error.path.join('.')}_${error.code}_${error.message}`}
              >
                <span className="font-medium">{`${
                  error.path.length > 0 ? `${error.path.join('.')}:` : ''
                }`}</span>{' '}
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      </Render>

      <Modal.Footer>
        <Button
          disabled={!data || errors.length > 0}
          className="px-10 capitalize"
          onClick={handleSave}
        >
          Apply config
        </Button>
      </Modal.Footer>
    </Modal.Content>
  );

  return (
    <Modal ref={modalRef} content={content} onClose={handleClose}>
      <Tooltip>
        <div slot="trigger">
          <Button
            theme
            onClick={() => modalRef.current?.open()}
            aria-label="Import App Configuration"
            className="cursor-pointer opacity-50 transition-opacity will-change-transform hover:opacity-100"
          >
            <Icon icon={faUpload} className="text-[20px]" />
          </Button>
        </div>
        Import App Configuration
      </Tooltip>
    </Modal>
  );
};
